/**
 * KAN-883: вид CRM "Лиды по дням" (?view=stats): период кнопками и
 * календарём (CrmPeriodControl, по умолчанию 30 дней), фильтр менеджера, таблица по дням.
 * KAN-904: таблица важнее графика - график скрыт по умолчанию, кнопка "График" показывает
 * его над таблицей из тех же данных (без отдельного запроса), выбор запоминается (useStoredFlag).
 * Состояние и переходы - useCrmStats, компонент только рисует.
 * KAN-910: график в адресе ?chart=1|0 (useUrlFlag); без параметра - запомненный выбор
 * (localStorage crm:statsChartVisible) или скрыт, и он сразу пишется в адрес.
 */
import type { ReactNode } from 'react';
import { BarChart3, Loader2 } from 'lucide-react';
import { CrmLeadFilters } from '@/components/crm/CrmLeadFilters';
import { CrmPeriodControl } from '@/components/crm/CrmPeriodControl';
import { CrmStatsChart } from '@/components/crm/stats/CrmStatsChart';
import { CrmStatsTable } from '@/components/crm/stats/CrmStatsTable';
import { isNotFoundError } from '@/hooks/useCatalog';
import { useCrmStats } from '@/hooks/useCrmStats';
import { useUrlFlag } from '@/hooks/useUrlState';
import { CRM_STATS_PERIOD_CHOICES } from '@/lib/crmStats';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { cn } from '@/lib/utils';

const MESSAGE = 'rounded-lg border bg-white p-6 text-sm';
export const CRM_STATS_CHART_STORAGE_KEY = 'crm:statsChartVisible';
/** KAN-910: параметр адреса графика. */
export const CRM_STATS_CHART_PARAM = 'chart';

export function CrmStatsSection({ leading }: { leading?: ReactNode }): JSX.Element {
  const stats = useCrmStats();
  const { data, isLoading, error } = stats.query;
  const [chartVisible, toggleChart] = useUrlFlag(CRM_STATS_CHART_PARAM, CRM_STATS_CHART_STORAGE_KEY, false);

  let content: JSX.Element;
  if (isLoading) {
    content = (
      <div className="flex justify-center py-10" data-testid="crm-stats-loading">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  } else if (isNotFoundError(error)) {
    content = <div className={`${MESSAGE} text-gray-600`}>CRM доступна только в режиме интернет-магазина.</div>;
  } else if (error || !data) {
    content = (
      <div className={`${MESSAGE} text-red-600`} data-testid="crm-stats-error">
        Не удалось загрузить статистику: {extractErrorMessage(error, 'ошибка сервера')}
      </div>
    );
  } else {
    content = (
      <>
        {chartVisible && <CrmStatsChart days={data.days} />}
        <CrmStatsTable stats={data} onDrill={stats.drill} />
      </>
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-end gap-3">
        {leading}
        <CrmPeriodControl
          label="Период"
          choices={CRM_STATS_PERIOD_CHOICES}
          range={stats.range}
          onChange={stats.setRange}
          onPreset={stats.setPreset}
          testId="crm-stats-period"
          showLabel
        />
        <CrmLeadFilters state={stats.leadFilters} showSearch={false} />
        <button
          type="button"
          aria-pressed={chartVisible}
          onClick={toggleChart}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors',
            chartVisible ? 'border-blue-200 bg-blue-50 text-blue-700' : 'bg-white text-gray-600 hover:text-gray-900'
          )}
        >
          <BarChart3 className="h-4 w-4" aria-hidden="true" />
          График
        </button>
      </div>
      {content}
    </>
  );
}
